import { Button, DialogTrigger } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployeesInfinite } from '@/entities/employee';
import { useAddEmployeesToGroup } from '@/entities/employeeGroups';
import type { EmployeeListItem } from '@/entities/conversations/model/api/types';
import { useDebouncedSearch } from '@/shared/hooks';
import { getOutsideTotal } from '@/shared/lib';
import { SelectionDialog, useLocalSelectionItems, useSelectionDialogState } from '@/shared/ui';
import { getEmployeeName } from './employeeName';

const getEmployeeId = (employee: EmployeeListItem) => employee.id;
const getEmployeeExternalId = (employee: EmployeeListItem) => employee.externalId;

type Props = {
  groupId: number;
  /** Идентификаторы операторов, уже состоящих в группе */
  existingEmployeeIds: number[];
};

/**
 * Кнопка «Добавить оператора» и окно выбора операторов для группы. Оператор набирается поиском
 * по всему набору на сервере; уже состоящие в группе операторы не предлагаются.
 */
export const AddOperatorsDialog: FC<Props> = ({ groupId, existingEmployeeIds }) => {
  const { t } = useTranslation(['common', 'employees']);
  const { mutate: addEmployees, isPending: isAdding } = useAddEmployeesToGroup();
  const { isOpen, search, setSearch, selected, handleOpenChange, handleToggle } = useSelectionDialogState({
    getItemId: getEmployeeId,
  });
  const appliedSearch = useDebouncedSearch(search);

  // Поиск по ФИО идёт по всему набору: оператор за пределами первой порции иначе недостижим
  const { data, isLoading } = useEmployeesInfinite(appliedSearch || undefined);
  const loadedEmployees: EmployeeListItem[] = useMemo(() => data?.pages.flatMap((p) => p.employees) ?? [], [data]);

  const items = useLocalSelectionItems({
    items: loadedEmployees,
    getItemId: getEmployeeId,
    excludedIds: existingEmployeeIds,
  });

  const handleConfirm = () => {
    addEmployees(
      { id: groupId, body: { employeeIds: selected.map(getEmployeeId) } },
      { onSuccess: () => handleOpenChange(false) },
    );
  };

  return (
    <SelectionDialog<EmployeeListItem>
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm" className="ml-auto">
            {t('employees:groups.addOperator')}
          </Button>
        </DialogTrigger>
      }
      title={t('employees:groups.addOperators')}
      search={search}
      onSearchChange={setSearch}
      items={items}
      // Операторы группы за выдачей окну неизвестны: из общего числа вычитаются только попавшие в неё
      total={getOutsideTotal(data?.pages[0]?.total, loadedEmployees.length, loadedEmployees.length - items.length)}
      isLoading={isLoading}
      selected={selected}
      onToggle={handleToggle}
      getItemId={getEmployeeId}
      renderTitle={getEmployeeName}
      renderHint={getEmployeeExternalId}
      labels={{
        searchPlaceholder: t('employees:groups.searchOperatorPlaceholder'),
        empty: t('common:noItemsToSelect'),
        notFound: t('employees:groups.notFound'),
        confirm: t('common:add'),
      }}
      onConfirm={handleConfirm}
      isConfirming={isAdding}
    />
  );
};
